import { useCallback, useEffect, useMemo, useState } from 'react';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { Svc, SvcType, getUomLabel } from 'domains/svcDomain';
import { Space, Tooltip } from 'antd';
import { QuestionCircleTwoTone } from '@ant-design/icons';
import { RevxButton } from 'components/RevxButton';

export const SVCBaseTable = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.loading.effects.svc.getAllBaseSVCs
  );

  const data = useAppSelector((state) => state.svc.baseSelectState.data);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pagedData = useMemo(() => {
    return (
      data?.slice((currentPage - 1) * pageSize, currentPage * pageSize) || []
    );
  }, [currentPage, data, pageSize]);

  const baseColumns: TableColumnsType<Svc> = [
    {
      title: t('common.revxId'),
      dataIndex: 'revxId',
      width: 80,
    },
    {
      title: t('platform.svc.tableColumn.type'),
      dataIndex: 'svcType',
      width: 120,
      render: (svcType: SvcType) => {
        return svcType?.name;
      },
    },
    {
      title: t('platform.svc.tableColumn.name'),
      width: 120,
      dataIndex: 'name',
    },
    {
      title: (
        <Space>
          {t('platform.svc.tableColumn.uom')}
          <Tooltip title={t('platform.svc.tooltip.uom')}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </Space>
      ),
      width: 120,
      dataIndex: 'uom',
      render: (text) => {
        return getUomLabel(text);
      },
    },
    {
      title: (
        <Space>
          {t('platform.svc.tableColumn.uomCoefficient')}
          <Tooltip title={t('platform.svc.tooltip.uomCoefficient')}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </Space>
      ),
      width: 120,
      dataIndex: 'uomCoefficient',
      render: (val) => {
        return (val || '').split('_').join(' ');
      },
    },
  ];

  const actionColumns: TableColumnsType<Svc> = [
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => {
              dispatch.svc.updateBaseSelectItem(record);
            }}
          >
            {t('platform.svc.baseSelect.select')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize);
            setCurrentPage(1);
          }

          if (pagination.current !== currentPage) {
            setCurrentPage(pagination.current);
          }

          break;
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    dispatch.svc.getAllBaseSVCs();
  }, [dispatch.svc]);

  return (
    <RevxTable<Svc>
      loading={loading}
      columns={[...baseColumns, ...actionColumns]}
      size="small"
      pagination={{
        pageSize: pageSize,
        total: data?.length,
        current: currentPage,
        showTotal: (total) => t('common.pagerTotal', { total: total }),
      }}
      indentSize={20}
      width={'auto'}
      minWidth={0}
      scroll={{
        y: 'calc(100vh - 220px)',
      }}
      dataSource={pagedData || []}
      onChange={onTableChange}
    />
  );
};
